import React from 'react';

/** Точка ряда: месяц в формате `yyyy-MM` и одно-два значения. */
export interface MonthlyPoint {
    month: string;
    primary: number;
    secondary?: number;
}

/**
 * Столбчатый график по месяцам с одной или двумя сериями.
 *
 * Библиотеки графиков в проекте нет, и заводить её ради нескольких столбиков не стоит —
 * поэтому один переиспользуемый компонент вместо повторения разметки в каждой секции.
 * Две серии рисуются рядом, а не стопкой: «куплено» и «выбыло» сравнивают между собой,
 * а не складывают.
 */
export default function MonthlyBarChart({points, primaryLabel, secondaryLabel, format, testId}: {
    points: MonthlyPoint[];
    primaryLabel: string;
    secondaryLabel?: string;
    /** Подпись значения над столбцом; по умолчанию — само число. */
    format?: (value: number) => string;
    testId?: string;
}) {
    if (points.length === 0) {
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid={`${testId}-empty`}>
                Данных за период нет.
            </p>
        );
    }
    const label = format ?? ((v: number) => (v === 0 ? '' : String(v)));
    const max = Math.max(1, ...points.flatMap(p => [p.primary, p.secondary ?? 0]));
    const hasSecondary = secondaryLabel !== undefined;

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded bg-brand-500 dark:bg-brand-400"/>
                    {primaryLabel}
                </span>
                {hasSecondary && (
                    <span className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded bg-amber-500 dark:bg-amber-400"/>
                        {secondaryLabel}
                    </span>
                )}
            </div>
            <div
                className="flex items-end gap-2 h-32 rounded-xl border border-gray-200 dark:border-ink-700 p-3 overflow-x-auto"
                data-testid={testId}
                role="img"
                aria-label={`${primaryLabel}${hasSecondary ? ` и ${secondaryLabel}` : ''} по месяцам`}
            >
                {points.map(p => (
                    <div key={p.month} className="flex-1 min-w-[24px] flex flex-col items-center justify-end h-full">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">
                            {label(p.primary)}
                        </span>
                        <div className="w-full flex items-end justify-center gap-0.5 h-full">
                            <div
                                className="flex-1 rounded-t bg-brand-500 dark:bg-brand-400 min-h-[2px]"
                                style={{height: `${(p.primary / max) * 100}%`}}
                                title={`${p.month} · ${primaryLabel}: ${p.primary}`}
                                data-testid={`${testId}-primary-${p.month}`}
                            />
                            {hasSecondary && (
                                <div
                                    className="flex-1 rounded-t bg-amber-500 dark:bg-amber-400 min-h-[2px]"
                                    style={{height: `${((p.secondary ?? 0) / max) * 100}%`}}
                                    title={`${p.month} · ${secondaryLabel}: ${p.secondary ?? 0}`}
                                    data-testid={`${testId}-secondary-${p.month}`}
                                />
                            )}
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1">{p.month.slice(5)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
